import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import ConfirmationDialog from '../components/ui/ConfirmationDialog';
import { agentCategoryService } from '../services/agentCategoryService';
import { departmentService } from '../services/departmentService';
import { industryService } from '../services/industryService';
import { agentInstanceService, agentService } from '../services/agentService';
import banner1 from '../assets/agents/banner-1.png';
import banner2 from '../assets/agents/banner-2.png';
import banner3 from '../assets/agents/banner-3.png';
import banner4 from '../assets/agents/banner-4.png';
import banner5 from '../assets/agents/banner-5.png';
import banner6 from '../assets/agents/banner-6.png';
import banner7 from '../assets/agents/banner-7.png';
import banner8 from '../assets/agents/banner-8.png';
import banner9 from '../assets/agents/banner-9.png';
import { Page, PageHeader, Section } from '../components/layout/primitives';
import { AgentCard } from '../components/agents/AgentCard';
import { Button } from '../components/ui/Button';
import { Checkbox, SelectField, TextArea, TextField } from '../components/ui/Field';
import { Overlay } from '../components/ui/Overlay';
import { Surface } from '../components/ui/Surface';

type AgentRole = 'entry' | 'functional';
type EntryType = 'text' | 'inbound-voice' | 'outbound-voice' | 'whatsapp';

type FormValues = {
  name: string;
  agentTypeId: string;
  departmentId: string;
  industryId?: string;
  categoryId?: string;
  shortDescription?: string;
  description?: string;
  useImageInChat?: boolean;
  agentRole?: AgentRole;
  entryType?: EntryType;
};

const CARD_BANNERS = [banner1, banner2, banner3, banner4, banner5, banner6, banner7, banner8, banner9];

const DEPARTMENT_FALLBACK = 'Marketing & Communication';

export const Agents: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editAgent, setEditAgent] = useState<any | null>(null);
  const [instances, setInstances] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const [categoryOptions, setCategoryOptions] = useState<{ id: string; name: string }[]>([]);
  const [typeOptions, setTypeOptions] = useState<{ id: string; name: string }[]>([]);
  const [industryOptions, setIndustryOptions] = useState<{ id: string; name: string }[]>([]);
  const [agentCategoryOptions, setAgentCategoryOptions] = useState<{ id: string; name: string }[]>([]);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      name: '',
      agentTypeId: '',
      departmentId: '',
      industryId: '',
      categoryId: '',
      shortDescription: '',
      description: '',
      useImageInChat: false,
      agentRole: 'functional',
      entryType: 'text',
    },
  });

    const watchedRole = watch('agentRole');


  const loadDepartments = async () => {
    try {
      const res: any = await departmentService.list();
      const data = res?.departments || [];
      setCategoryOptions(data.map((d: any) => ({ id: String(d.id), name: d.name })));
    } catch {
      toast.error('Failed to load departments');
    }
  };

  useEffect(() => {
    loadDepartments();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res: any = await industryService.list({ limit: 100, isActive: true });
        const raw = res?.industries || res?.data?.industries || [];
        const normalized = Array.isArray(raw)
          ? raw.map((i: any) => ({ id: String(i.id), name: String(i.name) }))
          : [];
        if (!cancelled) setIndustryOptions(normalized);
      } catch {
        toast.error('Failed to load industries');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res: any = await agentCategoryService.list({ limit: 100, isActive: true });
        const raw = res?.agentCategories || res?.data?.agentCategories || [];
        const normalized = Array.isArray(raw)
          ? raw.map((i: any) => ({ id: String(i.id), name: String(i.name) }))
          : [];
        if (!cancelled) setAgentCategoryOptions(normalized);
      } catch {
        toast.error('Failed to load categories');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res: any = await agentService.listAgentTypes?.();
        const raw = (res && (res.agentTypes || res.data?.agentTypes || res.data || res)) || [];
        const normalized = Array.isArray(raw)
          ? raw.map((t: any) => ({ id: String(t.id), name: String(t.name ?? t.code ?? t.id) }))
          : [];
        if (!cancelled && normalized.length) setTypeOptions(normalized);
      } catch (err) {
        console.warn('Failed to load agent types', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadInstances = async () => {
    setLoading(true);
    try {
      const res: any = await agentInstanceService.listAgentInstance?.({ search: searchTerm });
      const raw = (res && (res.data?.agentInstances || res.agentInstances || res.data || res)) || [];

      const normalized: any[] = Array.isArray(raw)
        ? raw.map((i: any) => ({
            id: i.id,
            instanceId: i.id,
            name: i.name || i.code || 'Unnamed Agent',
            type: (i.agentType && (i.agentType.name || i.agentType.code)) || 'Unknown',
            typeId: i.agentType?.id || i.agentTypeId || '',
            departmentId: i.departmentId,
            industryId: i.industryId || i.industry?.id,
            categoryId: i.categoryId || i.category?.id,
            shortDescription: i.shortDescription || '',
            description: i.description || '',
            status: i.isActive ? 'active' : 'inactive',
            state: i.state || '',
            tasksCompleted: i.metrics?.tasksCompleted || 0,
            successRate: i.metrics?.successRate || 0,
            updatedAt: i.updatedAt,
            deletedAt: i?.deletedAt || null,
          }))
        : [];

      setInstances(normalized);
    } catch (err) {
      console.error('Failed to load agent instances', err);
      setInstances([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => {
      loadInstances();
    }, 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    loadInstances();
  }, []);

  useEffect(() => {
    if (!editAgent) return;
    reset({
      name: editAgent.name || '',
      agentTypeId: String(editAgent.typeId || ''),
      departmentId: String(editAgent.departmentId || ''),
      industryId: String(editAgent.industryId || ''),
      categoryId: String(editAgent.categoryId || ''),
      shortDescription: editAgent.shortDescription || '',
      useImageInChat: false,
      description: editAgent.description || '',
      agentRole: editAgent.metadata?.agentRole || 'functional',
      entryType: editAgent.metadata?.entryType || 'text',
    });
  }, [editAgent, typeOptions, categoryOptions, reset]);

  const openCreate = () => {
    setEditAgent(null);
    reset({
      name: '',
      agentTypeId: typeOptions[0]?.id || '',
      departmentId: categoryOptions[0]?.id || '',
      industryId: industryOptions[0]?.id || '',
      categoryId: agentCategoryOptions[0]?.id || '',
      shortDescription: '',
      useImageInChat: false,
      description: '',
      agentRole: 'functional',
      entryType: 'text',
    });
    setIsCreateOpen(true);
  };

  const openEdit = async (agent: any) => {
    setEditAgent(agent);
    reset({
      name: agent.name || '',
      agentTypeId: String(agent.typeId || ''),
      departmentId: String(agent.departmentId || ''),
      industryId: String(agent.industryId || ''),
      categoryId: String(agent.categoryId || ''),
      shortDescription: agent.shortDescription || '',
      useImageInChat: agent.useImageInChat || false,
      description: agent.description || '',
      agentRole: agent.metadata?.agentRole || 'functional',
      entryType: agent.metadata?.entryType || 'text',
    });
    setValue('agentTypeId', String(agent.typeId || ''));
    setIsCreateOpen(true);

    // Fetch full agent to get complete metadata (list response may have partial data)
    try {
      const resp = await agentInstanceService.getAgentInstanceById(encodeURIComponent(agent.instanceId || agent.id), { includeDeleted: true });
      const full = (resp && (resp as any).data) ? (resp as any).data : resp;
      if (!full) return;
      const fullAgent = { ...agent, metadata: full.metadata || agent.metadata || {} };
      setEditAgent(fullAgent);
      reset({
        name: full.name || agent.name || '',
        agentTypeId: String(full.agentTypeId || agent.typeId || ''),
        departmentId: String(full.departmentId || agent.departmentId || ''),
        useImageInChat: full.useImageInChat || agent.useImageInChat || false,
        description: full.description || agent.description || '',
        agentRole: full.metadata?.agentRole || 'functional',
        entryType: full.metadata?.entryType || 'text',
      });
    } catch { /* non-blocking: list data already populated */ }
  };

  const generateCodeFromName = (name: string) => {
    return name.trim().toUpperCase().replace(/\s+/g, '_');
  };

  const onSubmit = async (data: FormValues) => {
    setSaving(true);
    try {
      const payload: any = {
        name: data.name,
        description: data.description || '',
        shortDescription: data.shortDescription || '',
        isActive: true,
        code: generateCodeFromName(data.name || 'AGENT'),
        departmentId: data.departmentId || undefined,
        industryId: data.industryId || undefined,
        categoryId: data.categoryId || undefined,
        agentTypeId: data.agentTypeId || undefined,
        useImageInChat: data?.useImageInChat || false,
        metadata: {
          // Preserve existing metadata (e.g. twilioConfig) when editing
          ...(editAgent ? (editAgent.metadata || {}) : {}),
          agentRole: data.agentRole || 'functional',
          ...(data.agentRole === 'entry' ? { entryType: data.entryType || 'text' } : {}),
        },
      };

      if (editAgent && editAgent.instanceId) {
        const res: any = await agentInstanceService.updateAgentInstance(editAgent.instanceId, payload);
        toast.success(res?.message || 'Agent updated successfully!');
      } else {
        const res: any = await agentInstanceService.createAgentInstance?.(payload);
        toast.success(res?.message || 'Agent created successfully!');
      }

      setIsCreateOpen(false);
      setEditAgent(null);
      loadInstances();
    } catch (err: any) {
      console.error('Failed to save agent instance', err);
      toast.error(err?.message || 'Failed to save agent.');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (id: string) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const doDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      const res: any = await agentInstanceService.deleteAgentInstance?.(deleteId);
      setInstances((prev) => prev.filter((i) => i.instanceId !== deleteId && i.id !== deleteId));
      setConfirmOpen(false);
      setDeleteId(null);
      toast.success(res?.message || 'Agent deleted successfully!');
    } catch (err) {
      console.error('Failed to delete agent instance', err);
      toast.error('Failed to delete agent. See console for details.');
    } finally {
      setDeleting(false);
    }
  };

  const filteredAgents = useMemo(() => {
    return (instances || []).filter((agent) => {
      const matchesSearch =
        agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agent.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agent.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || agent.status === statusFilter;
      const matchesCategory = categoryFilter === 'all' || agent.departmentId === categoryFilter;
      return matchesSearch && matchesStatus && matchesCategory;
    });
  }, [instances, searchTerm, statusFilter, categoryFilter]);

  const totalAgents = instances.length;
  const departmentNameById = useMemo(() => {
    return categoryOptions.reduce<Record<string, string>>((acc, department) => {
      acc[department.id] = department.name;
      return acc;
    }, {});
  }, [categoryOptions]);

  return (
    <Page
      containerSize="content"
      backgroundClassName="relative min-h-0 overflow-hidden bg-[linear-gradient(180deg,#FFFFFF_0%,#4CC4D012_100%)]"
      contentClassName="pb-10 pt-6"
    >
      <PageHeader
        className="py-10"
        title={
          <>
            Explore your <span className="bg-gradient-to-r from-c-805dc6 to-c-3683c6 bg-clip-text text-transparent">AI Pods</span>
          </>
        }
        subtitle="Discover and manage your deployed AI agents, track performance, and configure the right assistants for every workflow."
        aside={
          <div className="flex items-end gap-3 lg:pb-2">
            <p className="text-[4.4rem] font-semibold leading-none text-transparent [-webkit-text-stroke:2px_#b7bdf1] sm:text-[5.3rem] lg:text-[6.1rem]">
              {totalAgents}
            </p>
            <p className="pb-2 text-xl font-medium text-c-53586b [writing-mode:vertical-rl] [transform:rotate(180deg)]">AI agents</p>
          </div>
        }
      />

      <Section className="grid gap-4 border-b border-c-e3e7f3 pb-6 pt-0 lg:grid-cols-[1fr_auto] lg:items-center">
          <div className="relative w-full max-w-[640px]">
            <TextField
              type="text"
              placeholder="Search agents by name, task, or category"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded-xl border border-c-c3c3c3 bg-white px-4 py-3 pr-11 text-[1rem] text-c-1f1f1f placeholder:text-c-9095a9 focus:border-c-8f8f8f focus:outline-none"
            />
            <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-c-5f79ce">⌕</span>
          </div>

          <div className="flex flex-wrap gap-3 lg:justify-end">
            <SelectField
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
              className="min-w-[160px] rounded-xl border border-c-c3c3c3 bg-white px-4 py-3 text-[1rem] text-c-1f1f1f"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </SelectField>

            <SelectField
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="min-w-[170px] rounded-xl border border-c-c3c3c3 bg-white px-4 py-3 text-[1rem] text-c-1f1f1f"
            >
              <option value="all">All Categories</option>
              {categoryOptions.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </SelectField>

            <Button tone="brand-gradient" type="button" onClick={openCreate} className="rounded-xl px-5 py-3 text-[1rem] font-medium">+ New Agent</Button>
          </div>
      </Section>

      <Section className="pb-14 pt-8">
          {loading ? (
            <div className="py-20 text-center text-c-70768f">Loading agents...</div>
          ) : (
            <div className="grid grid-cols-1 justify-items-center gap-7 md:grid-cols-2 xl:grid-cols-3">
              {filteredAgents.map((agent, index) => (
                <AgentCard
                  key={agent.instanceId || agent.id}
                  agent={agent}
                  banner={CARD_BANNERS[index % CARD_BANNERS.length]}
                  departmentName={departmentNameById[agent.departmentId || ''] || DEPARTMENT_FALLBACK}
                  onEdit={openEdit}
                  onDelete={confirmDelete}
                />
              ))}
            </div>
          )}

          {filteredAgents.length === 0 && !loading && (
            <div className="py-20 text-center">
              <div className="text-6xl">🤖</div>
              <h3 className="mt-3 text-2xl font-semibold text-c-1f2430">No agents found</h3>
              <p className="mt-2 text-c-6e748c">Try changing your search keywords or filters.</p>
            </div>
          )}
      </Section>

      {isCreateOpen && (
        <Overlay className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => {
              setIsCreateOpen(false);
              setEditAgent(null);
            }}
          />
          <Surface tone="popover" className="relative mx-4 max-h-[85vh] w-full max-w-3xl overflow-y-auto rounded-2xl border p-6">
            <div className="mb-4">
              <h2 className="text-xl font-semibold text-agentic-text-primary">{editAgent ? 'Edit Agent' : 'Create AI Agent'}</h2>
              <p className="mt-1 text-sm text-agentic-text-secondary">Define or update an agent instance.</p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-agentic-text-primary">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <TextField
                    {...register('name', {
                      required: 'Name is required',
                      minLength: { value: 2, message: 'Name must be at least 2 characters' },
                    })}
                    placeholder="e.g., Research Agent"
                    className={`w-full rounded-lg border px-3 py-2 ${
                      errors.name ? 'border-red-300' : 'border-gray-200'
                    } focus:border-transparent focus:ring-2 focus:ring-agentic-primary-blue`}
                  />
                  {errors.name && <div className="mt-1 text-xs text-red-600">{errors.name.message}</div>}
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-agentic-text-primary">
                    Agent Type<span className="text-red-500"> *</span>
                  </label>
                  <SelectField
                    {...register('agentTypeId', { required: 'Agent type is required' })}
                    className={`w-full rounded-lg border px-3 py-2 ${
                      errors.agentTypeId ? 'border-red-300' : 'border-gray-200'
                    } focus:border-transparent focus:ring-2 focus:ring-agentic-primary-blue`}
                  >
                    <option value="">-- Select agent type --</option>
                    {typeOptions.length === 0 ? (
                      <option value="">-- No types available --</option>
                    ) : (
                      typeOptions.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))
                    )}
                  </SelectField>
                  {errors.agentTypeId && <div className="mt-1 text-xs text-red-600">{errors.agentTypeId.message}</div>}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-agentic-text-primary">Short Description</label>
                  <TextField
                    {...register('shortDescription', {
                      maxLength: { value: 255, message: 'Short description max length is 255' },
                    })}
                    placeholder="e.g., Handles first-touch customer queries"
                    className={`w-full rounded-lg border px-3 py-2 ${
                      errors.shortDescription ? 'border-red-300' : 'border-gray-200'
                    } focus:border-transparent focus:ring-2 focus:ring-agentic-primary-blue`}
                  />
                  {errors.shortDescription && <div className="mt-1 text-xs text-red-600">{errors.shortDescription.message}</div>}
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-agentic-text-primary">
                    Department<span className="text-red-500"> *</span>
                  </label>
                  <SelectField
                    {...register('departmentId', { required: 'Department is required' })}
                    className={`w-full rounded-lg border px-3 py-2 ${
                      errors.departmentId ? 'border-red-300' : 'border-gray-200'
                    } focus:border-transparent focus:ring-2 focus:ring-agentic-primary-blue`}
                  >
                    <option value="">-- Select department --</option>
                    {categoryOptions.length === 0 ? (
                      <option value="">-- No categories available --</option>
                    ) : (
                      categoryOptions.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))
                    )}
                  </SelectField>
                  {errors.departmentId && <div className="mt-1 text-xs text-red-600">{errors.departmentId.message}</div>}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-agentic-text-primary">Industry</label>
                  <SelectField tone="default-focus-primary"
                    {...register('industryId')}
                    className="w-full rounded-lg border px-3 py-2 focus:ring-2"
                  >
                    <option value="">-- Select industry --</option>
                    {industryOptions.map((i) => (
                      <option key={i.id} value={i.id}>
                        {i.name}
                      </option>
                    ))}
                  </SelectField>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-agentic-text-primary">Category</label>
                  <SelectField tone="default-focus-primary"
                    {...register('categoryId')}
                    className="w-full rounded-lg border px-3 py-2 focus:ring-2"
                  >
                    <option value="">-- Select category --</option>
                    {agentCategoryOptions.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </SelectField>
                </div>

                <div className="mt-6">
                  <label className="cursor-pointer rounded-md border p-2 hover:bg-gray-50">
                    <Checkbox
                     
                      {...register('useImageInChat')}
                      onChange={(e) => {
                        setValue('useImageInChat', e.target.checked);
                      }}
                      className="h-4 w-4"
                    />
                    <span className="ml-3 text-sm font-medium">Use image in chat</span>
                  </label>
                </div>
              </div>

              {/* Agent Role */}
              <div>
                <label className="block text-sm font-medium text-agentic-text-primary mb-2">Agent Role <span className="text-red-500">*</span></label>
                <div className="flex gap-3">
                  {(['functional', 'entry'] as AgentRole[]).map((role) => (
                    <label key={role} className="flex items-center gap-2 cursor-pointer px-3 py-2 border rounded-lg hover:bg-gray-50">
                      <input
                        type="radio"
                        value={role}
                        {...register('agentRole')}
                        className="text-agentic-primary-blue"
                      />
                      <span className="text-sm font-medium capitalize">{role === 'entry' ? 'Entry Level' : 'Functional'}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  {watchedRole === 'entry'
                    ? 'Entry agents receive incoming interactions (chat or voice) and pass them to the orchestrator.'
                    : 'Functional agents handle specific tasks routed by the orchestrator.'}
                </p>

              </div>


              {/* Entry Type (only for entry-level agents) */}
              {watchedRole === 'entry' && (
                <div>
                  <label className="block text-sm font-medium text-agentic-text-primary mb-2">Entry Type <span className="text-red-500">*</span></label>
                  <div className="flex gap-3 flex-wrap">
                    {([
                      { value: 'text', label: 'Text (Chat)' },
                      { value: 'inbound-voice', label: 'Inbound Voice' },
                      { value: 'outbound-voice', label: 'Outbound Voice' },
                      { value: 'whatsapp', label: 'WhatsApp' },
                    ] as { value: EntryType; label: string }[]).map((opt) => (
                      <label key={opt.value} className="flex items-center gap-2 cursor-pointer px-3 py-2 border rounded-lg hover:bg-gray-50">
                        <input
                          type="radio"
                          value={opt.value}
                          {...register('entryType')}
                          className="text-agentic-primary-blue"
                        />
                        <span className="text-sm font-medium">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    For voice agents, you can configure Twilio credentials under the Voice Config tab after creation.
                  </p>
                </div>
              )}

              <div>
                <label className="mb-1 block text-sm font-medium text-agentic-text-primary">Description</label>
                <TextArea tone="default-focus-primary"
                  {...register('description')}
                  placeholder="Short description of this agent"
                  rows={4}
                  className="w-full rounded-lg border px-3 py-2 focus:ring-2"
                />
              </div>

              <div className="flex items-center justify-end space-x-3 pt-2">
                <Button tone="outline-muted"
                  type="button"
                  onClick={() => {
                    setIsCreateOpen(false);
                    setEditAgent(null);
                  }}
                  className="rounded-lg border px-4 py-2"
                >
                  Cancel
                </Button>
                <Button tone="primary-gradient"
                  type="submit"
                  disabled={saving}
                  className="rounded-lg px-4 py-2"
                >
                  {saving ? 'Saving...' : editAgent ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </Surface>
        </Overlay>
      )}

      <ConfirmationDialog
        isOpen={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setDeleteId(null);
        }}
        onConfirm={doDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this agent instance?"
        confirmText="Delete"
        cancelText="Cancel"
        loading={deleting}
        variant="error"
      />
    </Page>
  );
};

export default Agents;
